import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useItemsStore, useLabelTemplatesStore, useSessionStore, useSitesStore } from "@/stores";
import IItemSiteSetting from "@/models/responses/IItemSiteSetting";
import IItemSiteSettingUpsertRequest from "@/models/requests/IItemSiteSettingUpsertRequest";
import Site from "@/models/frontend/Site";
import ItemSiteSetting from "@/models/frontend/ItemSiteSetting";
import Item from "@/models/frontend/Item";

export const useItemSiteSettingsStore = defineStore("itemSiteSettings", {
  state: () => ({
    error: "" as any,
    data: [] as ItemSiteSetting[],
    loadedSiteId: 0,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedSiteId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedSiteId === selectedSiteId.value) return;

      this.error = "";
      await api
        .get<IItemSiteSetting[]>(`v1.0/items/site-settings/${selectedSiteId.value}`)
        .then((response) => {
          this.data = response.data.map((itemSiteSetting) => this.mapToFrontend(itemSiteSetting));
          this.loadedSiteId = selectedSiteId.value;
        })
        .catch((error) => this.error = error);
    },
    async updateItemSiteSettings(item: Item, site: Site, itemSiteSettings: ItemSiteSetting) {
      const request = <IItemSiteSettingUpsertRequest>{
        siteCode: site.code,
        itemCode: item.code,
        defaultLabelQuantity: itemSiteSettings.defaultLabelQuantity,
        defaultLabelTemplateCode: itemSiteSettings.defaultLabelTemplate?.code,
        restockPullType: itemSiteSettings.restockPullType,
        canOrderManually: itemSiteSettings.canOrderManually ?? false
      }

      this.error = "";
      await api.put(`v1.0/items/${item.id}/site-settings/${site.id}`, request)
        .catch((error) => this.error = error);
    },
    mapToFrontend(itemSiteSetting: IItemSiteSetting) {
      const site = useSitesStore().getById(itemSiteSetting.siteId);
      const item = useItemsStore().getById(itemSiteSetting.itemId);
      const defaultLabelTemplate = itemSiteSetting.defaultLabelTemplate ? useLabelTemplatesStore().getById(itemSiteSetting.defaultLabelTemplate.id) : undefined;
      return new ItemSiteSetting(
        itemSiteSetting.id,
        site,
        item,
        itemSiteSetting.defaultLabelQuantity,
        defaultLabelTemplate,
        itemSiteSetting.restockPullType,
        itemSiteSetting.canOrderManually ?? false,
      );
    },
  },
  getters: {
    getByItemId:
      (state) => (itemId: number): ItemSiteSetting | undefined => {
        return state.data.find((x) => x.item?.id === itemId);
      }
  },
});
